import { useState } from 'react';
import { Link } from 'react-router-dom';
import { withStandardEarnings, withStandardDeductions } from '@payroll/shared';
import { PageLayout } from '../components/layout/PageLayout';
import { useSlipPeriods, useSlip } from '../hooks';
import { apiFetch } from '../lib/api-client';
import { Download } from 'lucide-react';
import { useToast } from '../components/ui/toast';
import { openFileUrl } from '../lib/liff';

interface PeriodRow { period: string; status: string; sent_at: string | null; }
interface SlipLine { label: string; amount: number; }
interface SlipData {
  period: string; gross: number; net: number;
  base_pay: number; ot_1_pay: number; ot_2_pay: number; ot_3_pay: number;
  additions_total: number;
  tax_amount: number; sso_amount: number; pf_amount: number;
  studentloan_amount: number; companyloan_amount: number;
  unpaid_leave_deduct: number; absent_deduct: number; manual_deductions: number;
  deductions_total: number;
  calculation_log?: { line_items?: { earnings?: SlipLine[]; deductions?: SlipLine[]; earnings_total?: number; deductions_total?: number }; ytd?: { gross: number; tax: number; sso: number; pf: number; net: number; welfare_fund?: number } } | null;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(n);
}

export function Slip() {
  const [period, setPeriod] = useState<string | null>(null);
  const { data: periods } = useSlipPeriods() as { data: PeriodRow[] | undefined };
  const { data: slip } = useSlip(period) as { data: SlipData | undefined };
  const toast = useToast();

  const [pdfLoading, setPdfLoading] = useState(false);
  // สองจังหวะ: แตะแรกสร้าง PDF · แตะสองเปิดไฟล์ — การเปิดต้องเกิดจากการแตะตรง ๆ ไม่ใช่หลัง await
  // (เบราว์เซอร์ในไลน์/iOS บล็อก window.open และ liff.openWindow ที่ตามหลัง await · TN fb#155)
  const [pdfReady, setPdfReady] = useState<{ period: string; url: string; at: number } | null>(null);
  const readyUrl = pdfReady && pdfReady.period === period && Date.now() - pdfReady.at < 4 * 60_000 ? pdfReady.url : null;
  async function preparePdf() {
    if (!period || pdfLoading) return;
    setPdfLoading(true);
    try {
      const { pdf_url } = await apiFetch<{ pdf_url: string }>(`/slip/pdf?period=${period}`);
      setPdfReady({ period, url: pdf_url, at: Date.now() });
    } catch {
      toast.error('สร้าง PDF ไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setPdfLoading(false);
    }
  }
  function openPdf() {
    if (!readyUrl) return;
    openFileUrl(readyUrl);
    setPdfReady(null);
  }

  if (!period) {
    return (
      <PageLayout title="สลิปเงินเดือน">
        <div className="space-y-2">
          {(periods ?? []).map((p) => (
            <button key={p.period} onClick={() => setPeriod(p.period)} className="card p-4 w-full text-left hover:bg-gray-50">
              <p className="font-medium">{p.period}</p>
              <p className="text-xs text-gray-500">{p.status === 'sent' ? 'ส่งแล้ว' : 'ปิดรอบแล้ว'}</p>
            </button>
          ))}
          {(periods ?? []).length === 0 && <p className="text-gray-500">ยังไม่มีสลิป — สลิปจะขึ้นที่นี่หลังฝ่ายบุคคลปิดงวดเงินเดือนงวดนั้น</p>}
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={`สลิป ${period}`}>
      <button onClick={() => setPeriod(null)} className="text-sm text-brand-600 mb-3">← เลือกรอบอื่น</button>
      {!slip ? <p>กำลังโหลด...</p> : (() => {
        const li = slip.calculation_log?.line_items;
        const earnings: SlipLine[] = li?.earnings ?? [
          { label: 'เงินเดือน / ค่าจ้าง', amount: (slip.base_pay ?? 0) + (slip.unpaid_leave_deduct ?? 0) + (slip.absent_deduct ?? 0) },
          { label: 'ค่าล่วงเวลา (OT1)', amount: slip.ot_1_pay },
          { label: 'ค่าล่วงเวลา (OT2)', amount: slip.ot_2_pay },
          { label: 'ค่าล่วงเวลา (OT3)', amount: slip.ot_3_pay },
          { label: 'เงินเพิ่ม / เบี้ยเลี้ยง', amount: slip.additions_total },
        ].filter((e) => e.amount !== 0);
        const deductions: SlipLine[] = li?.deductions ?? [
          { label: 'หักลาไม่รับเงิน', amount: slip.unpaid_leave_deduct },
          { label: 'หักขาดงาน', amount: slip.absent_deduct },
          { label: 'ภาษีหัก ณ ที่จ่าย', amount: slip.tax_amount },
          { label: 'ประกันสังคม', amount: slip.sso_amount },
          { label: 'กองทุนสำรองเลี้ยงชีพ', amount: slip.pf_amount },
          { label: 'กยศ. / กรอ.', amount: slip.studentloan_amount },
          { label: 'เงินกู้บริษัท', amount: slip.companyloan_amount },
          { label: 'รายการหักอื่น ๆ', amount: slip.manual_deductions },
        ].filter((d) => d.amount !== 0);
        // #13 — tie each total to the array actually rendered: only trust the
        // stored *_total when the matching itemized array is present (it includes
        // §76 carry/credit lines that the scalar fallback array never has).
        // fb#158 — แถวมาตรฐานครบทุกงวด (0.00 ถ้าไม่มี) ตรงกับสลิป PDF · เติมหลังคิดยอดรวม (แถวเติม = 0 เสมอ)
        const earnRows = withStandardEarnings(earnings);
        const dedRows = withStandardDeductions(deductions);
        const earnTotal = li?.earnings ? (li.earnings_total ?? earnings.reduce((t, e) => t + e.amount, 0)) : earnings.reduce((t, e) => t + e.amount, 0);
        const dedTotal = li?.deductions ? (li.deductions_total ?? deductions.reduce((t, e) => t + e.amount, 0)) : deductions.reduce((t, e) => t + e.amount, 0);
        return (
        <div className="card p-4 space-y-3">
          <div>
            <p className="text-sm font-medium mb-1 text-green-700">รายได้</p>
            <div className="text-sm space-y-1 ml-2 text-gray-700">
              {earnRows.map((e, i) => <div key={i} className={`flex justify-between${e.amount === 0 ? ' text-gray-400' : ''}`}><span>{e.label}</span><span>{fmt(e.amount)}</span></div>)}
                          </div>
            <div className="flex justify-between border-t mt-1 pt-1 font-semibold"><span>รวมรายได้</span><span>{fmt(earnTotal)}</span></div>
          </div>
          <div>
            <p className="text-sm font-medium mb-1 text-red-600">รายการหัก</p>
            <div className="text-sm space-y-1 ml-2 text-gray-700">
              {dedRows.map((d, i) =>
                // fb#143 — บรรทัดยกไปงวดหน้า = ยอดที่ยังไม่หักงวดนี้ (ตรงกับหน้าต่างรายละเอียดฝั่ง HR) ไม่ใช่ "+เงินเพิ่ม"
                d.amount < 0 && d.label.startsWith('ยกไปงวดหน้า')
                  ? <div key={i} className="flex justify-between text-gray-500"><span>เลื่อนไปหักงวดหน้า</span><span>ไม่หักงวดนี้ {fmt(-d.amount)}</span></div>
                  : <div key={i} className={`flex justify-between${d.amount === 0 ? ' text-gray-400' : ''}`}><span>{d.label}</span><span>{d.amount === 0 ? fmt(0) : d.amount < 0 ? `+${fmt(-d.amount)}` : `-${fmt(d.amount)}`}</span></div>)}
            </div>
            <div className="flex justify-between border-t mt-1 pt-1 font-semibold"><span>รวมรายการหัก</span><span>-{fmt(dedTotal)}</span></div>
          </div>
          <div className="flex justify-between border-t pt-2 text-lg font-bold text-brand-700">
            <span>เงินสุทธิ</span><span>{fmt(slip.net)}</span>
          </div>
          {slip.calculation_log?.ytd && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-500">ยอดสะสมตั้งแต่ต้นปี (YTD)</p>
              <div className="flex justify-between"><span>รายได้สะสม</span><span>{fmt(slip.calculation_log.ytd.gross)}</span></div>
              <div className="flex justify-between"><span>ภาษีสะสม</span><span>{fmt(slip.calculation_log.ytd.tax)}</span></div>
              <div className="flex justify-between"><span>ประกันสังคมสะสม</span><span>{fmt(slip.calculation_log.ytd.sso)}</span></div>
              <div className="flex justify-between"><span>กองทุนสำรองสะสม</span><span>{fmt(slip.calculation_log.ytd.pf)}</span></div>
              {!!slip.calculation_log.ytd.welfare_fund && (
                <div className="flex justify-between"><span>กองทุนสงเคราะห์ลูกจ้างสะสม</span><span>{fmt(slip.calculation_log.ytd.welfare_fund)}</span></div>
              )}
              <div className="flex justify-between font-medium text-gray-700"><span>สุทธิสะสม</span><span>{fmt(slip.calculation_log.ytd.net)}</span></div>
            </div>
          )}
          {/* Conservative-withholding notice — ระบบหักภาษีเฉพาะลดหย่อนพื้นฐาน
              (60k + ค่าใช้จ่าย + SSO + PF). ผู้มีลดหย่อนเพิ่มต้องขอคืนตอนยื่น ภงด.91 */}
          {slip.tax_amount > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <p className="font-medium mb-0.5">ทำไมหักภาษีเท่านี้?</p>
              <p className="leading-relaxed">
                ระบบหักภาษีโดยคำนวณจากลดหย่อนพื้นฐาน
                (ส่วนตัว 60,000 + ค่าใช้จ่าย + ปกส. + PF) เท่านั้น —
                <strong> ยังไม่รวม</strong> คู่สมรส/บุตร/ประกัน/RMF/SSF/ดอกเบี้ยบ้าน.
                หากมีรายการเหล่านี้ คุณสามารถ <strong>ขอคืนตอนยื่น ภงด.91</strong> ปลายปีได้
              </p>
              <Link to="/factsheet#tax" className="inline-block mt-1 underline text-amber-700 hover:text-amber-900">
                ดูรายละเอียดวิธีคิดภาษี →
              </Link>
            </div>
          )}
          {readyUrl ? (
            <button onClick={openPdf} className="btn-cta w-full">
              <Download className="w-4 h-4" /> เปิด PDF
            </button>
          ) : (
            <button onClick={preparePdf} disabled={pdfLoading} className="btn-cta w-full">
              <Download className="w-4 h-4" /> {pdfLoading ? 'กำลังสร้าง PDF…' : 'ดาวน์โหลด PDF'}
            </button>
          )}
          <p className="text-xs text-center text-gray-500">ไฟล์จะเปิดในเบราว์เซอร์ของโทรศัพท์ แล้วกดแชร์/บันทึกเก็บไว้ได้ — กด "ดาวน์โหลด PDF" รอสักครู่ แล้วกด "เปิด PDF"</p>
          <div className="text-xs text-center text-gray-500 space-x-3">
            <Link to="/factsheet#tax" className="hover:text-brand-700 underline">ภาษี</Link>
            <Link to="/factsheet#sso" className="hover:text-brand-700 underline">ประกันสังคม</Link>
            <Link to="/factsheet#pf" className="hover:text-brand-700 underline">กองทุน</Link>
            <Link to="/factsheet#ytd" className="hover:text-brand-700 underline">YTD</Link>
          </div>
        </div>
        );
      })()}
    </PageLayout>
  );
}
